import React from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { FaFilter, FaPlus, FaQuestionCircle, FaTimes, FaTrash } from "react-icons/fa";
import ConditionRow from "../../common/ConditionRow";
import { getFieldByName, isOperatorValueRequired } from "../../../utils/conditionBuilderUtils";

const ListViewFilterPanel = ({
  externalFilters,
  externalFilterLogic,
  availableFields,
  /** When false, title row is omitted (e.g. parent Modal provides Modal.Header). */
  showTitleBar = true,
  onClose,
  onAddFilter,
  onRemoveFilter,
  onUpdateFilter,
  onUpdateLogic,
  onApplyFilters,
  onClearFilters,
  setShowDateHelpModal
}) => {
  return (
    <div
      className="filter-sidebar-content listview-filter-panel-inner"
      style={{
        display: 'flex',
        flexDirection: 'column',
        padding: showTitleBar ? '20px' : '16px 20px 20px',
        width: '100%',
        minWidth: 0,
        ...(showTitleBar ? { maxHeight: '100%', overflow: 'hidden' } : {})
      }}
    >
      {showTitleBar && (
        <div className="filter-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid #e5e5e5' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#032d60', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FaFilter />
            Quick Filters
          </h3>
          <button
            type="button"
            className="close-btn"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              color: '#706e6b',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <FaTimes size={18} />
          </button>
        </div>
      )}

      <div
        className="filter-body"
        style={{
          flex: 1,
          overflowX: 'hidden',
          paddingRight: '8px',
          minHeight: 0,
          ...(showTitleBar ? { overflowY: 'auto' } : { overflowY: 'visible' })
        }}
      >
        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="mb-0">Filter Logic</h6>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={onAddFilter}
            >
              <FaPlus className="me-1" />
              Add Filter
            </Button>
          </div>

          <Form.Select
            value={externalFilterLogic}
            onChange={(e) => onUpdateLogic(e.target.value)}
            className="mb-3"
          >
            <option value="AND">AND (All conditions must be true)</option>
            <option value="OR">OR (Any condition can be true)</option>
          </Form.Select>
        </div>

        {externalFilters.length === 0 ? (
          <div className="text-center text-muted py-4">
            <FaFilter className="mb-2" size={24} />
            <p>No filters configured</p>
            <small>Click &quot;Add Filter&quot; to create filtering rules</small>
          </div>
        ) : (
          <div className="filters-container">
            {externalFilters.map((filter, index) => {
              const selectedField = getFieldByName(availableFields, filter.field, { nameKey: 'field_name' });
              const dt = (selectedField?.data_type || '').toLowerCase();
              const showDateHint = selectedField && (dt === 'date' || dt === 'datetime') &&
                isOperatorValueRequired(filter.operator);

              return (
                <div key={filter.id} className="filter-row border rounded p-3 mb-3 bg-light">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <h6 className="mb-0">Filter {index + 1}</h6>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => onRemoveFilter(filter.id)}
                    >
                      <FaTrash />
                    </Button>
                  </div>

                  <Row className="align-items-start">
                    <Col xs={12}>
                      <ConditionRow
                        condition={filter}
                        fields={availableFields}
                        onChange={(idx, key, val) => {
                          const row = externalFilters[idx];
                          if (row) onUpdateFilter(row.id, key, val);
                        }}
                        index={index}
                        readOnly={false}
                        options={{
                          fieldKey: 'field',
                          nameKey: 'field_name',
                          labelKey: 'field_label',
                          fieldColSize: 4,
                          operatorColSize: 3,
                          valueColSize: 5,
                          useSearchableDropdown: true,
                        }}
                      />
                      {showDateHint && (
                        <div className="small text-muted mt-1 d-flex align-items-center gap-1">
                          <FaQuestionCircle
                            className="text-primary"
                            size={12}
                            style={{ cursor: 'pointer' }}
                            title="Date filter help"
                            onClick={() => setShowDateHelpModal(true)}
                          />
                          <span
                            role="button"
                            tabIndex={0}
                            className="text-primary"
                            style={{ cursor: 'pointer' }}
                            onClick={() => setShowDateHelpModal(true)}
                            onKeyDown={(e) => e.key === 'Enter' && setShowDateHelpModal(true)}
                          >
                            Date / datetime literals and formats
                          </span>
                        </div>
                      )}
                    </Col>
                  </Row>
                </div>
              );
            })}
          </div>
        )}

        <div className="filter-actions" style={{
          marginTop: 'auto',
          paddingTop: '16px',
          borderTop: '1px solid #e5e5e5',
          display: 'flex',
          gap: '8px'
        }}>
          <Button
            variant="outline-secondary"
            onClick={onClearFilters}
            style={{ flex: 1 }}
          >
            Clear All
          </Button>
          <Button
            variant="primary"
            onClick={onApplyFilters}
            style={{ flex: 1 }}
          >
            Apply Filters
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ListViewFilterPanel;
